import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, ConfigProvider, Checkbox, Breadcrumb, Tooltip, Modal, Typography, Spin, Empty, Segmented } from "antd";
import DataTable from "../components/Tables/Datatable";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { useApiCall } from "../services/api";
import { useDispatch, useSelector } from "react-redux";
import { AppstoreOutlined, BarsOutlined } from "@ant-design/icons";
import BookCaseModal from "../components/models/BookCase";
import { useGlobalModal } from "../context/SuccessModalProvider";
import ProductImageName from "../components/Layout/ProductImageName";
import PrimaryButton from "../components/Forms/PrimaryButton";
import { formatDate, handleRouteNav } from "../utils";
import Filters from "../components/Filters";
import SearchInput from "../components/Forms/SearchInput";
import Loader from "../common/Loader";
import { PurgeButton, PurgeModalUI, usePurgeModal } from "../components/models/PurgeModal";
import { notificationRefresh } from "../redux/auth/cartRefreshSlice";
import CustomBadge from "../components/Charts/CustomBadge";
import { AllStockPagesButtons } from "./AllStock";
import { shirtingCompCodes } from "../constant";
import { SuitingShirtingToggle } from "../components/SuitingShirtingToggle";

const { Title } = Typography;

const AllFoldings = () => {
  const { showPopup } = useGlobalModal();
  const { getApi, postApi, loading } = useApiCall();
  const dispatch = useDispatch();
  const location = useLocation();
  const fromNotification = location.state?.fromNotification || false;
  const { sort_no, short_name, clienttype } = location.state || {};
  const { full_name, role_id, user_id, is_sales_person, is_sales_manager, is_admin, is_customer, role, customer_id } = useSelector(
    (state) => state.auth,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBookCaseModalOpen, setIsBookCaseModalOpen] = useState(false);
  const [allStocks, setAllStocks] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [bookCasesNotifOpen, setBookCasesNotifOpen] = useState(fromNotification);
  const [bookCasesNotifLoading, setBookCasesNotifLoading] = useState(false);
  const [bookCasesNotifications, setBookCasesNotifications] = useState([]);
  const [isOutOfStockModalVisible, setIsOutOfStockModalVisible] = useState(false);
  const [outOfStockCases, setOutOfStockCases] = useState([]);

  const [filterProductData, setFilterProductData] = useState([]);
  const [isShirtingSelected, setIsShirtingSelected] = useState(false);

  const purgeHook = usePurgeModal();

  const payloadRoles = {
    // 1: "admin",
    1: "sales_manager",
    2: "customer",
    3: "sales_person",
    4: "sales_manager",
  };

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { appliedFilters, ModalUI, FilterButton, resetFilters } = Filters({
    filters: [
      {
        type: "select",
        key: "status",
        label: "Status",
        inputProps: { placeholder: "Select Status" },
        options: [
          { value: "open", label: "Open" },
          { value: "booked", label: "Booked" },
          { value: "ordered", label: "Ordered" },
        ],
      },
      {
        type: "select",
        key: "quality_name",
        label: "Product",
        inputProps: { placeholder: "Search For Products" },
        options: filterProductData.data
          ?.filter((item) => shirtingCompCodes.includes(item.company_code) === isShirtingSelected)
          .map((item) => ({
            label: item.quality_name,
            value: item.sort_no,
          })),
      },
      {
        type: "select",
        key: "shade_no",
        label: "Shade",
        inputProps: { placeholder: "Search For Shades" },
        options: (isShirtingSelected ? filterProductData.shade_shirting_data : filterProductData.shade_data)?.map((item) => ({
          label: item.shade_no,
          value: item.shade_no,
        })),
      },
    ],
    onChange: () => setPagination((prev) => ({ ...prev, current: 1 })),
  });

  const fetchIdRef = useRef(0);

  const fetchAllStocks = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      const { status, quality_name, shade_no } = appliedFilters || {};

      const filters = {
        filter: status,
        quality_name,
        shade_no,
      };

      const apiEndpoint = isShirtingSelected ? "sales/shirting/get-all-folding-stock-shade-shirting" : "sales/product/get-all-folding-stock";

      setStocksLoading(true);
      const response = await postApi(apiEndpoint, {
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchValue?.trim() || undefined,
        ...(Object.keys(appliedFilters).length ? filters : {}),
      });

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const dataWithKeys = (response.data || []).map((item, index) => ({
        ...item,
        key: item.id || index,
      }));

      setAllStocks(dataWithKeys);
      setPagination((prev) => ({
        ...prev,
        total: response.total || 0,
      }));
      setSelectedRowKeys([]);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error("Error fetching client details:", error);
    } finally {
      if (currentFetchId !== fetchIdRef.current) return;
      setStocksLoading(false);
    }
  }, [sort_no, pagination.current, pagination.pageSize, appliedFilters, searchValue, isShirtingSelected]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    resetFilters();
    setSearchValue("");
    setSelectedStockIds([]);
    setSelectedRowKeys([]);
  }, [isShirtingSelected]);

  useEffect(() => {
    const handler = setTimeout(fetchAllStocks, 300);
    return () => clearTimeout(handler);
  }, [fetchAllStocks]);

  const fetchBookCasesNotifications = useCallback(async () => {
    try {
      setBookCasesNotifLoading(true);

      const paginate = {
        notification_type: "Booked Folding",
        role: payloadRoles[role_id],
        ...(is_customer ? { customer_id } : is_sales_person ? { user_id } : {}),
      };

      const res = await postApi(is_customer ? "customer/notification" : "sales/common/notification", paginate);

      const sorted = [...(res?.data || [])].sort((a, b) => b.id - a.id);
      setBookCasesNotifications(sorted);
    } catch (err) {
      console.error("Error fetching book case notifications:", err);
      setBookCasesNotifications([]);
    } finally {
      setBookCasesNotifLoading(false);
    }
  }, []);

  const unSeenHistory = bookCasesNotifications.filter((item) => {
    return is_sales_manager ? !item.is_salesmanger : is_admin ? !item.is_admin : is_customer ? !item.is_customer : !item.is_salesperson;
  });

  const fetchFilterProductData = async () => {
    try {
      setStocksLoading(true);
      const res = await getApi("sales/product/get-product-name");
      setFilterProductData(res);
    } catch (err) {
      console.error("Error fetching filter product data:", err);
    } finally {
      setStocksLoading(false);
    }
  };

  useEffect(() => {
    fetchBookCasesNotifications();
    fetchFilterProductData();
  }, []);

  useEffect(() => {
    if (bookCasesNotifOpen && unSeenHistory.length > 0 && !is_admin) {
      const markAsSeen = async () => {
        try {
          const res = await postApi("sales/common/view-notification", {
            id: unSeenHistory.map((item) => item.id),
            role: is_customer ? "Customer" : role,
          });

          if (res?.status) {
            dispatch(notificationRefresh());
          }
        } catch (err) {
          console.error("Error marking notification as seen:", err);
        }
      };

      markAsSeen();
    }
  }, [bookCasesNotifOpen, unSeenHistory.length]);

  const handlePurge = async (payload) => {
    const response = await postApi("sales/shade/delete-book-folding-stock", payload, true);

    if (response && response.status) {
      fetchAllStocks();
    }

    return response.status || false;
  };

  const handleChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.pageSize !== prev.pageSize ? 1 : paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
    // setFilteredInfo(filters);
    // setSortedInfo(sorter);
  };

  const handleRow = (record) => {
    console.log("Row clicked:", record);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      width: 15,
    },

    {
      title: "Shade",
      dataIndex: "shadeno",
      key: "shadeno",
      render: (text, record) => (
        <ProductImageName img_url={record?.shade_picture} img_alt={record?.ph_shadeno || text} text={record?.ph_shadeno || text} />
      ),
    },
    {
      title: "Quality Name",
      dataIndex: "qualityname",
      key: "qualityname",
      render: (text) => text || "-",
    },
    {
      title: "Barcode",
      dataIndex: "bar_code",
      key: "bar_code",
      align: "center",
      render: (text) => <span className="font-manrope font-semibold text-[14px] leading-[120%] tracking-[0%] text-black">{text}</span>,
    },
    {
      title: "Meters",
      dataIndex: "graded_mtrs",
      align: "center",
      key: "graded_mtrs",
      className: "text-gray-600",
    },

    {
      title: "Status",
      key: "status",
      width: 300,
      render: (_, record) => (
        <span className="capitalize whitespace-normal">
          {record?.status != "open" && record?.message
            ? record?.message.split(",").map((item) => {
                return (
                  <>
                    <div>{item}</div>
                  </>
                );
              })
            : record?.status != "open"
              ? record?.status
              : "Open"}
        </span>
      ),
    },

    {
      title: "",
      key: "select",
      align: "right",
      fixed: "right",
      render: (_, record) => (
        <div className="flex justify-end pr-6">
          <Tooltip title={record?.status == "booked" ? "Already booked" : record?.status == "ordered" ? "Already ordered" : ""}>
            <Checkbox
              className="custom-checkbox"
              disabled={record?.status != "open"}
              checked={record?.status != "open" || selectedRowKeys.includes(record.key)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedRowKeys([...selectedRowKeys, record.key]);
                } else {
                  setSelectedRowKeys(selectedRowKeys.filter((k) => k !== record.key));
                }
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const selectAll = () => {
    if (selectedRowKeys.length > 0 && selectedRowKeys.length === allStocks.filter((item) => item?.status === "open").length) {
      setSelectedRowKeys([]); // Deselect all
    } else {
      setSelectedRowKeys(allStocks.filter((item) => item?.status === "open").map((item) => item.key)); // Select all
    }
  };

  const handleBookCases = () => {
    const selectedRows = allStocks.filter((item) => item?.status === "open" && selectedRowKeys.includes(item.key));
    const stockIdsRecords = selectedRows;

    if (stockIdsRecords.length === 0) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please select at least one folding to book.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return; // ⛔ Stop here, don't open modal
    }

    setSelectedStockIds(stockIdsRecords);
    setIsBookCaseModalOpen(true);
  };

  const handleModalSubmit = async (selectedModalRows) => {
    if (!selectedModalRows) {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Please select a customer</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
      return;
    }

    const customer_id = selectedModalRows?.customer_id;

    const payload = {
      customer_id,
      folding_stock_id: selectedStockIds.map((s) => s.folding_stock_id).join(","),
      folding_stock_bar_code: selectedStockIds.map((s) => s.bar_code).join(","),
      updated_by: full_name,
      updated_id: is_sales_person ? user_id : undefined,
    };

    try {
      const response = await postApi("sales/shade/add-book-folding-stock", payload, true);

      if (response.status) {
        setSelectedRowKeys([]);
        setSelectedStockIds([]);
        setIsBookCaseModalOpen(false);
        dispatch(notificationRefresh());
      } else if (response?.data?.length > 0) {
        setIsBookCaseModalOpen(false);
        setIsOutOfStockModalVisible(true);
        setOutOfStockCases(response.data);
      }
      fetchAllStocks();
      fetchBookCasesNotifications();
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
    }
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Stocks");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("/dashboard", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Folding",
              href: handleRouteNav("/product-catalog/folding", role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <ModalUI />
      <>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#A33434",
              borderRadius: 8,
            },
            components: {
              Table: {
                headerBg: "#ffffff",
                headerColor: "#6b7280",
                borderColor: "#f3f4f6",
                headerBorderRadius: 0,
              },
              Checkbox: {
                borderRadiusSM: 4,
                controlInteractiveSize: 18,
              },
            },
          }}
        >
          <div className="mx-auto bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="px-12 py-6 !pb-0 flex justify-between gap-5">
              <div className="text-left">
                <h2 className="text-3xl font-semibold text-gray-800 mb-1 font-abhaya">{"FOLDING"}</h2>
              </div>
              <div>
                <div className="flex items-center justify-end gap-4">
                  <PrimaryButton
                    btnType="secondary"
                    onClick={handleBookCases}
                    text="Book Folding"
                    title={selectedRowKeys.length === 0 ? "Please select at least one case" : ""}
                    disabled={selectedRowKeys.length === 0}
                    loading={loading}
                  />
                  <Button type="text" danger onClick={selectAll} className="text-sm">
                    {selectedRowKeys.length > 0 && selectedRowKeys.length === allStocks.filter((item) => item?.status === "open").length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-7 items-center px-12 py-6">
              <SearchInput value={searchValue} onChange={handleOnSearchChange} placeholder="Search for barcodes" />
              <FilterButton />
            </div>

            <div className="flex w-full justify-between items-center px-12 mb-5">
              <AllStockPagesButtons activeButton="foldings" role_id={role_id} />
              <SuitingShirtingToggle value={isShirtingSelected} onChange={setIsShirtingSelected} />
            </div>

            {/* Table */}

            {stocksLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader size={60} />{" "}
              </div>
            ) : (
              <div className="px-6 pb-6">
                <DataTable
                  columns={columns}
                  data={allStocks}
                  pagination={pagination}
                  handleChange={handleChange}
                  handleRow={handleRow}
                  className="stock-table"
                  disableHover
                />

                <div className="flex justify-end mt-4 gap-3">
                  {!is_sales_person && <PurgeButton hookData={purgeHook} buttonLabel="Purge Folding" />}
                  <PrimaryButton
                    onClick={() => setBookCasesNotifOpen(true)}
                    text="Booked Folding History"
                    iconOnleft
                    btnType="ghost"
                    iconClassName="!bg-[#a334341a] hover:!bg-[#a334341a]"
                    icon={
                      <img
                        onClick={() => setBookCasesNotifOpen(true)}
                        src="/assets/icons/ArrowClockwise.svg"
                        alt="Article"
                        className="cursor-pointer w-5 h-5"
                      />
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </ConfigProvider>
      </>
      <BookCaseModal
        open={isBookCaseModalOpen}
        onClose={() => setIsBookCaseModalOpen(false)}
        handleSubmit={handleModalSubmit}
        title="Book Folding"
        description="Barcodes"
        short_name={short_name}
        no_of_cases={selectedStockIds.length}
        loadingProp={loading}
      />

      <PurgeModalUI hookData={purgeHook} onConfirm={handlePurge} loading={loading} title="Purge Folding" />

      <Modal
        className="form-css custom-modal my-5"
        open={bookCasesNotifOpen}
        footer={null}
        onCancel={() => {
          setBookCasesNotifOpen(false);
          fetchBookCasesNotifications();
        }}
        centered
        width={500}
      >
        <Title level={2} className="font-abhaya mb-3">
          Booked Folding History
        </Title>

        {bookCasesNotifLoading ? (
          <div className="flex justify-center items-center py-10">
            <Spin size="large" />
          </div>
        ) : bookCasesNotifications.length === 0 ? (
          <Empty description="No notifications found" />
        ) : (
          bookCasesNotifications.map((item, index) => (
            <div key={index} className="rounded-2xl border border-[#D9D9D9] p-3 flex flex-col justify-between mb-3">
              <div className="grid gap-1">
                <div className="flex justify-between">
                  <p className="text-[#000000] text-lg font-semibold">{item.title}</p>
                  {(is_sales_manager ? !item.is_salesmanger : is_admin ? false : is_customer ? !item.is_customer : !item.is_salesperson) && (
                    <CustomBadge text={"New"} variant="primary" />
                  )}
                </div>
                <p className="text-[#666666]">{formatDate(item.created_at, { format: "D MMM YYYY h:mma" })}</p>
                <div className="mt-2 text-[#666666] whitespace-pre-line">{item.long_message}</div>
              </div>
            </div>
          ))
        )}
      </Modal>

      <Modal className="form-css" open={isOutOfStockModalVisible} footer={null} onCancel={() => setIsOutOfStockModalVisible(false)} width={400}>
        <Title className="input-css font-abhaya" level={2}>
          Important Information!
        </Title>
        <div className="">
          <span className=" text-gray-500">The following barcodes were not found in stock:</span>
          <div className=" text-gray-500 px-2">{outOfStockCases.join(", ")}</div>
        </div>
      </Modal>
    </>
  );
};

export default AllFoldings;
